import type { PhaseInfo, IntakePhase, WorkflowStage } from '../types/workflow'

export const INTAKE_PHASES: Partial<Record<IntakePhase, PhaseInfo>> = {
  1: { phase: 1, name: 'Qualify', description: 'GETTING TO KNOW YOU' },
  2: { phase: 2, name: 'Understand', description: 'YOUR GOALS' },
  3: { phase: 3, name: 'Personalize', description: 'BUILDING YOUR PLAN' },
  4: { phase: 4, name: 'Safety', description: 'KEEPING YOU SAFE' },
  5: { phase: 5, name: 'Enroll', description: 'ALL DONE' },
}

export const CLINIC_GLP1_WORKFLOW_STAGES: WorkflowStage[] = [
  {
    id: 'ww-basics',
    name: 'basics',
    stageType: 'form',
    stageTemplate: 'basics',
    orderNo: 1,
    phase: 1,
    templateVariables: [
      { title: 'header', type: 'text', value: "Great! Let's start here" },
      { title: 'subHeader', type: 'text', value: 'Tell me your origin story, the short version.' },
    ],
    answerMapping: { field: 'basics', type: 'object' },
  },
  {
    id: 'ww-bmi-calculator',
    name: 'bmi_calculator',
    stageType: 'form',
    stageTemplate: 'bmi_calculator',
    orderNo: 2,
    phase: 1,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Now, a little more about you' },
      { title: 'subHeader', type: 'text', value: 'These details just help me personalize your care plan.' },
    ],
    answerMapping: { field: 'bmi_data', type: 'object' },
  },
  {
    id: 'ww-goals',
    name: 'goals',
    stageType: 'single_choice',
    stageTemplate: 'goals',
    orderNo: 3,
    phase: 2,
    templateVariables: [],
    answerMapping: { field: 'primary_goal', type: 'string' },
  },
  {
    id: 'ww-glp1-experience',
    name: 'glp1_experience',
    stageType: 'single_choice',
    stageTemplate: 'radio_list',
    orderNo: 4,
    phase: 3,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Have you tried a GLP-1 before?' },
      {
        title: 'subHeader',
        type: 'text',
        value: 'This just helps me understand where you are in your journey.',
      },
      {
        title: 'options',
        type: 'array',
        value: [
          { id: 'currently_taking', label: "Yes, I'm currently on one" },
          { id: 'took_in_past', label: 'Yes, but I stopped' },
          { id: 'first_time', label: 'No, this would be my first time' },
          { id: 'not_sure', label: "Honestly, I'm not sure" },
        ],
      },
    ],
    answerMapping: { field: 'glp1_experience', type: 'string' },
  },
  {
    id: 'ww-current-glp1-medication',
    name: 'current_glp1_medication',
    stageType: 'form',
    stageTemplate: 'which_medications',
    orderNo: 5,
    phase: 4,
    conditions: [
      {
        type: 'answer',
        field: 'glp1_experience',
        operator: 'in',
        value: ['currently_taking', 'took_in_past'],
      },
    ],
    templateVariables: [
      { title: 'header', type: 'text', value: 'Got it — which ones have you tried?' },
      {
        title: 'subHeader',
        type: 'text',
        value: "This helps me understand what's worked for you and what hasn't.",
      },
      {
        title: 'options',
        type: 'array',
        value: [
          { id: 'ozempic', label: 'Ozempic® (Semaglutide)' },
          { id: 'wegovy', label: 'Wegovy® (Semaglutide)' },
          { id: 'mounjaro', label: 'Mounjaro® (Tirzepatide)' },
          { id: 'zepbound', label: 'Zepbound® (Tirzepatide)' },
          { id: 'compounded_semaglutide', label: 'Compounded Semaglutide [CarniSema®]' },
          { id: 'compounded_tirzepatide', label: 'Compounded Tirzepatide [CarniTide®]' },
          { id: 'other', label: 'Other / Not sure' },
        ],
      },
    ],
    answerMapping: { field: 'current_glp1_medications', type: 'array' },
  },
  {
    id: 'ww-glp1-dosage',
    name: 'dosage',
    stageType: 'form',
    stageTemplate: 'dosage',
    orderNo: 6,
    phase: 4,
    conditions: [
      {
        type: 'answer',
        field: 'glp1_experience',
        operator: 'in',
        value: ['currently_taking', 'took_in_past'],
      },
    ],
    templateVariables: [],
    answerMapping: { field: 'glp1_dosages', type: 'object' },
  },
  {
    id: 'ww-current-glp1-details',
    name: 'current_glp1_details',
    stageType: 'form',
    stageTemplate: 'last_dose',
    orderNo: 7,
    phase: 4,
    conditions: [{ type: 'answer', field: 'glp1_experience', operator: 'equals', value: 'currently_taking' }],
    templateVariables: [],
    answerMapping: { field: 'glp1_duration', type: 'string' },
  },
  {
    id: 'ww-past-glp1',
    name: 'past_glp1',
    stageType: 'form',
    stageTemplate: 'last_dose',
    orderNo: 8,
    phase: 4,
    conditions: [
      {
        type: 'answer',
        field: 'glp1_experience',
        operator: 'in',
        value: ['took_in_past', 'previously_took'],
      },
    ],
    templateVariables: [],
    answerMapping: { field: 'glp1_last_dose_timing', type: 'string' },
  },
  {
    id: 'ww-glp1-experience-details',
    name: 'experience',
    stageType: 'form',
    stageTemplate: 'experience',
    orderNo: 9,
    phase: 4,
    conditions: [
      {
        type: 'answer',
        field: 'glp1_experience',
        operator: 'in',
        value: ['took_in_past', 'previously_took'],
      },
    ],
    templateVariables: [],
    answerMapping: { field: 'experience_data', type: 'object' },
  },
  {
    id: 'ww-therapy-preview',
    name: 'therapy_preview',
    stageType: 'static',
    stageTemplate: 'therapy_preview',
    orderNo: 10,
    phase: 3,
    templateVariables: [],
  },
  {
    id: 'ww-plan-selection',
    name: 'medicine_selection',
    stageType: 'selection',
    stageTemplate: 'medicine_selection',
    orderNo: 11,
    phase: 5,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Choose your preferred medicine' },
      {
        title: 'options',
        type: 'array',
        value: [
          { id: 'carnisema', label: 'CarniSema' },
          { id: 'carnitide', label: 'CarniTide' },
        ],
      },
    ],
    answerMapping: { field: 'product_selection', type: 'object' },
  },
  {
    id: 'ww-safety-screening',
    name: 'safety_screening',
    stageType: 'form',
    stageTemplate: 'safety_screening',
    orderNo: 12,
    phase: 4,
    templateVariables: [],
    answerMapping: { field: 'safety_screening', type: 'object' },
  },
  {
    id: 'ww-medical-conditions',
    name: 'medical_conditions',
    stageType: 'multiple_choice',
    stageTemplate: 'checkbox_list',
    orderNo: 13,
    phase: 4,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Do any of these sound familiar?' },
      {
        title: 'subHeader',
        type: 'text',
        value: 'Check anything that applies — it helps me make sure your treatment is safe and tailored to you.',
      },
      {
        title: 'options',
        type: 'array',
        value: [
          { id: 'type2_diabetes', label: 'Type 2 Diabetes' },
          { id: 'hypertension', label: 'Hypertension (High Blood Pressure)' },
          { id: 'heart_disease', label: 'Heart Disease' },
          { id: 'sleep_apnea', label: 'Sleep Apnea' },
          { id: 'pcos', label: 'PCOS (Polycystic Ovary Syndrome)' },
          { id: 'fatty_liver', label: 'Fatty Liver Disease (NAFLD)' },
          { id: 'depression', label: 'Depression / Anxiety' },
          { id: 'joint_pain', label: 'Joint Pain / Arthritis' },
          { id: 'kidney_disease', label: 'Kidney Disease' },
          { id: 'none', label: 'None of the above', exclusive: true },
        ],
      },
    ],
    answerMapping: { field: 'medical_conditions', type: 'array' },
  },
  {
    id: 'ww-current-medications',
    name: 'current_medications',
    stageType: 'form',
    stageTemplate: 'smart_medication_picker',
    orderNo: 14,
    phase: 4,
    templateVariables: [],
    answerMapping: { field: 'medication_data', type: 'object' },
  },
  {
    id: 'ww-medication-allergies',
    name: 'medication_allergies',
    stageType: 'form',
    stageTemplate: 'medication_allergies_picker',
    orderNo: 15,
    phase: 4,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Any allergies to medications I should know about?' },
      { title: 'subHeader', type: 'text', value: "Even if it's minor \u2014 better safe than sorry!" },
    ],
    answerMapping: { field: 'medication_allergies', type: 'string' },
  },
  {
    id: 'ww-id-upload',
    name: 'id_upload',
    stageType: 'file_upload',
    stageTemplate: 'id_upload',
    orderNo: 16,
    phase: 5,
    templateVariables: [],
    answerMapping: { field: 'id_photo_key', type: 'string' },
  },
  {
    id: 'ww-body-photo',
    name: 'body_photo',
    stageType: 'file_upload',
    stageTemplate: 'body_photo',
    orderNo: 17,
    phase: 5,
    templateVariables: [],
    answerMapping: { field: 'body_photo_key', type: 'string' },
  },
  {
    id: 'ww-shipping-billing',
    name: 'shipping_billing_address',
    stageType: 'form',
    stageTemplate: 'address_form',
    orderNo: 18,
    phase: 5,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Where should we ship your medication?' },
      { title: 'subHeader', type: 'text', value: 'We also need a billing address for payment processing.' },
    ],
    answerMapping: { field: 'shipping_billing_data', type: 'object' },
  },
  {
    id: 'ww-review-submit',
    name: 'review_submit',
    stageType: 'static',
    stageTemplate: 'review_submit',
    orderNo: 19,
    phase: 5,
    templateVariables: [],
  },
  {
    id: 'ww-payment-method',
    name: 'payment_method',
    stageType: 'form',
    stageTemplate: 'payment_method',
    orderNo: 20,
    phase: 5,
    templateVariables: [
      { title: 'header', type: 'text', value: 'Almost there!' },
      { title: 'subHeader', type: 'text', value: 'Review and submit your assessment.' },
    ],
    answerMapping: { field: 'payment_method_data', type: 'object' },
  },
]
